import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { formatLocaleDate, formatLocaleDateTime } from './stateDistributionDetail';
import {
  drawBrandStripe,
  drawPageFooter,
  drawPageHeader,
  drawSectionBox,
  loadReportLogo,
  PDF_COLORS,
  setPdfType,
  wrapTextLines,
} from '../pdfReportLayout';

const MARGIN = 16;
const HEADER_OFFSET = 26;
const FOOTER_OFFSET = 14;

function buildFilterLines(filters = {}, labels = {}, locale = 'en-US') {
  const lines = [];
  if (filters.dateFrom || filters.dateTo) {
    const from = filters.dateFrom ? formatLocaleDate(filters.dateFrom, locale) : '…';
    const to = filters.dateTo ? formatLocaleDate(filters.dateTo, locale) : '…';
    lines.push(`${labels.dateRange || 'Date range'}: ${from} – ${to}`);
  }
  if (filters.vendorLabel) {
    lines.push(`${labels.vendor || 'Representative'}: ${filters.vendorLabel}`);
  }
  if (filters.historicalMode) {
    lines.push(`${labels.historicalMode || 'Historical'}: ${labels.yes || 'Yes'}`);
  }
  if (lines.length === 0) {
    lines.push(labels.noFiltersApplied || 'No filters applied');
  }
  return lines;
}

function drawCoverPage(doc, {
  title,
  labels = {},
  locale = 'en-US',
  filters = {},
  rows = [],
  detailCount = 0,
  printedBy = '',
  logo = null,
}) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  let cursorY = drawPageHeader(doc, { logo, margin: MARGIN });

  setPdfType(doc, 'caption');
  doc.text(labels.reportSubtitle || 'State distribution report', MARGIN, cursorY);
  cursorY += 6;

  if (printedBy) {
    doc.text(`${labels.printedBy || 'Printed by'}: ${printedBy}`, MARGIN, cursorY);
    cursorY += 5;
  }

  const printedOn = formatLocaleDateTime(new Date(), locale);
  doc.text(`${labels.printedOn || 'Printed on'}: ${printedOn}`, MARGIN, cursorY);
  cursorY += 10;

  setPdfType(doc, 'display');
  const titleLines = wrapTextLines(doc, title, contentWidth);
  doc.text(titleLines, MARGIN, cursorY);
  cursorY += titleLines.length * 7 + 6;

  const filterLines = buildFilterLines(filters, labels, locale);
  const filterBoxHeight = 10 + filterLines.length * 5.5;
  drawSectionBox(doc, MARGIN, cursorY, contentWidth, filterBoxHeight);

  setPdfType(doc, 'title');
  doc.text(labels.appliedFilters || 'Applied filters', MARGIN + 5, cursorY + 7);
  setPdfType(doc, 'body');
  let innerY = cursorY + 13;
  filterLines.forEach((line) => {
    doc.text(`• ${line}`, MARGIN + 7, innerY);
    innerY += 5.5;
  });
  cursorY += filterBoxHeight + 8;

  const summaryLines = [
    `${labels.totalRecords || 'Total records'}: ${detailCount}`,
    ...rows.map((row) => `${row.label}: ${row.count} (${row.percentage}%)`),
  ];
  const summaryBoxHeight = 10 + summaryLines.length * 5.5;
  drawSectionBox(doc, MARGIN, cursorY, contentWidth, summaryBoxHeight);

  setPdfType(doc, 'title');
  doc.text(labels.summary || 'Summary', MARGIN + 5, cursorY + 7);
  setPdfType(doc, 'body');
  innerY = cursorY + 13;
  summaryLines.forEach((line) => {
    doc.text(`• ${line}`, MARGIN + 7, innerY);
    innerY += 5.5;
  });
}

async function addChartPage(doc, {
  element,
  labels = {},
  title,
  logo,
}) {
  if (!element) return;

  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    onclone: (clonedDocument) => {
      clonedDocument.documentElement.classList.remove('dark');
      const clonedCard = clonedDocument.getElementById(element.id);
      if (!clonedCard) return;
      clonedCard.style.boxShadow = 'none';
      clonedCard.style.margin = '0';
      clonedCard.style.maxWidth = '100%';
    },
  });

  doc.addPage();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let cursorY = drawPageHeader(doc, { logo, margin: MARGIN });

  setPdfType(doc, 'title');
  doc.text(labels.chartOverview || 'Chart overview', MARGIN, cursorY);
  cursorY += 4;

  setPdfType(doc, 'caption');
  doc.text(title, MARGIN, cursorY + 4);
  cursorY += 10;

  const imgData = canvas.toDataURL('image/png');
  const maxWidth = pageWidth - MARGIN * 2;
  const maxHeight = pageHeight - cursorY - FOOTER_OFFSET - 8;
  let imgWidth = maxWidth;
  let imgHeight = (canvas.height * imgWidth) / canvas.width;

  if (imgHeight > maxHeight) {
    imgHeight = maxHeight;
    imgWidth = (canvas.width * imgHeight) / canvas.height;
  }

  doc.addImage(imgData, 'PNG', MARGIN, cursorY, imgWidth, imgHeight);
}

function addDetailPages(doc, {
  labels = {},
  detailColumns = [],
  detailRecords = [],
  title,
  logo,
}) {
  doc.addPage();
  const contentTop = drawPageHeader(doc, { logo, margin: MARGIN });

  setPdfType(doc, 'title');
  doc.text(labels.detailedData || 'Detailed data', MARGIN, contentTop);
  setPdfType(doc, 'caption');
  doc.text(title, MARGIN, contentTop + 5);

  const head = [detailColumns.map((column) => column.label)];
  const body = detailRecords.length > 0
    ? detailRecords.map((record) => detailColumns.map((column) => record[column.key] ?? '—'))
    : [[labels.noDetailRecords || 'No records match the selected filters.']];

  autoTable(doc, {
    startY: contentTop + 10,
    head: detailRecords.length > 0 ? head : undefined,
    body,
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 2.8,
      textColor: PDF_COLORS.text,
      lineColor: PDF_COLORS.line,
      lineWidth: 0.1,
    },
    headStyles: {
      font: 'helvetica',
      fontStyle: 'bold',
      fontSize: 8,
      fillColor: PDF_COLORS.brand,
      textColor: PDF_COLORS.white,
    },
    bodyStyles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: PDF_COLORS.textBody,
    },
    alternateRowStyles: {
      fillColor: PDF_COLORS.brandLight,
    },
    margin: {
      left: MARGIN,
      right: MARGIN,
      top: HEADER_OFFSET + 6,
      bottom: FOOTER_OFFSET + 4,
    },
    showHead: 'everyPage',
    didDrawPage: () => {
      drawBrandStripe(doc, 3);
    },
  });
}

function applyFooters(doc, { labels, printedBy }) {
  const totalPages = doc.internal.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    drawPageFooter(doc, {
      labels,
      printedBy,
      pageNumber: page,
      totalPages,
      margin: MARGIN,
    });
  }
}

export async function exportStateDistributionPdf({
  title,
  rows = [],
  filters = {},
  labels = {},
  locale = 'en-US',
  filename = 'state-distribution.pdf',
  element = null,
  detailColumns = [],
  detailRecords = [],
  printedBy = '',
}) {
  const logo = await loadReportLogo(12);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  drawCoverPage(doc, {
    title,
    labels,
    locale,
    filters,
    rows,
    detailCount: detailRecords.length,
    printedBy,
    logo,
  });

  if (element) {
    await addChartPage(doc, { element, labels, title, logo });
  }

  addDetailPages(doc, { labels, detailColumns, detailRecords, title, logo });
  applyFooters(doc, { labels, printedBy });

  doc.save(filename);
}

export function exportStateDistributionExcel({
  title,
  rows = [],
  filters = {},
  labels = {},
  locale = 'en-US',
  filename = 'state-distribution.xlsx',
  detailColumns = [],
  detailRecords = [],
  printedBy = '',
}) {
  const filterLines = buildFilterLines(filters, labels, locale);
  const printedOn = formatLocaleDateTime(new Date(), locale);

  const summaryRows = [
    ['Enrola'],
    [title],
    [labels.reportSubtitle || 'State distribution report'],
    ...(printedBy ? [[`${labels.printedBy || 'Printed by'}: ${printedBy}`]] : []),
    [`${labels.printedOn || 'Printed on'}: ${printedOn}`],
    [],
    [labels.appliedFilters || 'Applied filters'],
    ...filterLines.map((line) => [line]),
    [],
    [labels.summary || 'Summary'],
    [`${labels.totalRecords || 'Total records'}: ${detailRecords.length}`],
    [],
    [labels.state || 'State', labels.count || 'Count', labels.percentage || '%'],
    ...rows.map((row) => [row.label, row.count, `${row.percentage}%`]),
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet['!cols'] = [{ wch: 32 }, { wch: 14 }, { wch: 12 }];

  const detailHead = detailColumns.map((column) => column.label);
  const detailBody = detailRecords.length > 0
    ? detailRecords.map((record) => detailColumns.map((column) => record[column.key] ?? '—'))
    : [[labels.noDetailRecords || 'No records match the selected filters.']];

  const detailSheet = XLSX.utils.aoa_to_sheet(
    detailRecords.length > 0 ? [detailHead, ...detailBody] : detailBody
  );
  detailSheet['!cols'] = detailColumns.map(() => ({ wch: 18 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, summarySheet, labels.summarySheetName || 'Summary');
  XLSX.utils.book_append_sheet(workbook, detailSheet, labels.detailSheetName || 'Detail');
  XLSX.writeFile(workbook, filename);
}
